import { Badge } from '@talentmesh/ui/components/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@talentmesh/ui/components/collapsible';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { cn } from '@talentmesh/ui/lib/utils';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import {
    EmailParticipantResponse,
    OutreachEmailThreadResponse,
    useOutreachEmailThread,
} from '../../../../../api/useOutreachCandidate';
import { useAiSearchProfilePopoutContext } from '../../../AiSearchProfilePopoutContext';
import SimpleTextFormatter from '../../SimpleTextFormatter';
import { toDateTimeString, toExplicitUTCDate } from '@/Utils/DateHelper';
import UIStrings from '@/Utils/UIStrings';

function formatEmailParticipant(participant: EmailParticipantResponse) {
    return participant.name ? `${participant.name} <${participant.email}>` : `<${participant.email}>`;
}

function formatEmailParticipants(participants: EmailParticipantResponse[] | null) {
    return participants ? participants.map((p) => formatEmailParticipant(p)).join(', ') : '';
}

function formatEmailTags(data: OutreachEmailThreadResponse | null | undefined): Record<string, number> {
    if (!data) {
        return {};
    }

    const emailIdsWithoutRootMessageId = data.emails.filter((x) => x.rootMessageId === null).map((x) => x.id);

    if (emailIdsWithoutRootMessageId.length === 0) {
        return {};
    }

    return Object.fromEntries(emailIdsWithoutRootMessageId.map((x, i) => [x, i + 1]));
}

const ProfileEmailThread = () => {
    const { selectedProfile } = useAiSearchProfilePopoutContext();
    const { data, isLoading } = useOutreachEmailThread(selectedProfile!.id);
    const emailTags = useMemo(() => formatEmailTags(data), [data]);

    if (isLoading) {
        return (
            <div className="flex justify-center py-6">
                <Spinner className="text-primary size-8" />
            </div>
        );
    }

    return (
        <div className="flex flex-col-reverse gap-2">
            {data?.emails.map(({ id, from, to, cc, bcc, subject, htmlBody, mailServerReceivedAt, rootMessageId }) => (
                <Collapsible key={id} className="group border-border overflow-hidden rounded-lg border">
                    <CollapsibleTrigger
                        type="button"
                        className={cn(
                            'flex w-full items-start gap-2 rounded-lg p-3 text-left transition-colors duration-200',
                            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                        )}
                    >
                        <ChevronRight
                            className="mt-1 size-4 shrink-0 transition-transform duration-200 ease-out group-data-[state=open]:rotate-90"
                            aria-hidden
                        />
                        <div className="min-w-0 flex-1 space-y-1">
                            <p className="text-xs">
                                {UIStrings.CapitalizedFrom}: {formatEmailParticipants(from)}
                            </p>
                            <div>
                                <p className="text-xs">
                                    {UIStrings.To}: {formatEmailParticipants(to)}
                                </p>
                                {cc && cc.length > 0 && (
                                    <p className="text-xs">
                                        {UIStrings.Cc}: {formatEmailParticipants(cc)}
                                    </p>
                                )}
                                {bcc && bcc.length > 0 && (
                                    <p className="text-xs">
                                        {UIStrings.Bcc}: {formatEmailParticipants(bcc)}
                                    </p>
                                )}
                                <p className="text-xs">{toDateTimeString(toExplicitUTCDate(mailServerReceivedAt))}</p>
                            </div>
                            <p className="text-sm font-semibold tracking-tight">{subject}</p>
                        </div>
                        {!rootMessageId && (
                            <Badge variant="secondary" className="ml-auto shrink-0 text-xs font-normal">
                                {`${UIStrings.Email} ${emailTags[id]}`}
                            </Badge>
                        )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="border-border bg-muted data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down border-t px-8 py-4 transition-all">
                        <SimpleTextFormatter
                            value={htmlBody}
                            className="whitespace-normal [&>ul]:list-disc [&>ul>li]:ml-8 [&>ul>li]:pl-0"
                        />
                    </CollapsibleContent>
                </Collapsible>
            ))}
        </div>
    );
};

export default ProfileEmailThread;
